import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getAudit } from '../utils/crmApi';
import { useToast } from '../components/Toast';
import '../crm.css';
import { contactPath, crmBasePath, dealPath, leadPath } from '../utils/crmEnv';

const AuditPage = () => {
    const history = useHistory();
    const { showError } = useToast();

    // State
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        entityType: '',
        action: '',
        search: '', // q matches summary
    });

    const pageSize = 50;

    const fetchAudit = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                ...filters
            };
            // Clean empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const result = await getAudit(params);
            setEvents(result.data || []);
            setTotal(result.total || 0);
        } catch (err) {
            console.error('Audit fetch error:', err);
            showError('Failed to load audit logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAudit();
    }, [page, filters]); // Re-fetch on filter/page change

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1); // Reset to page 1 on filter change
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleString();
    };

    const getEntityLink = (type, id) => {
        const basePath = crmBasePath();
        if (type === 'lead') return leadPath(id);
        if (type === 'contact') return contactPath(id);
        if (type === 'deal') return dealPath(id);
        if (type === 'property') return `${basePath}/properties/${id}`;
        return null;
    };

    return (
        <div className="crm-container">
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Audit Logs</h1>
                    <p style={{ color: 'var(--crm-gray-600)', margin: 0 }}>
                        Track system activities and changes ({total} events)
                    </p>
                </div>
                <div className="crm-page-actions">
                    <button className="crm-btn crm-btn-secondary" onClick={fetchAudit}>
                        Refresh
                    </button>
                </div>
            </div>

            <div className="crm-card" style={{ marginBottom: '24px' }}>
                <div className="crm-card-body">
                    <div className="crm-filters-row" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div className="crm-form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
                            <input
                                type="text"
                                className="crm-input"
                                placeholder="Search summary..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                        <div className="crm-form-group" style={{ marginBottom: 0, width: '150px' }}>
                            <select
                                className="crm-select"
                                value={filters.entityType}
                                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                            >
                                <option value="">All Entities</option>
                                <option value="lead">Lead</option>
                                <option value="contact">Contact</option>
                                <option value="deal">Deal</option>
                                <option value="activity">Activity</option>
                                <option value="property">Property</option>
                                <option value="file">File</option>
                            </select>
                        </div>
                        <div className="crm-form-group" style={{ marginBottom: 0, width: '150px' }}>
                            <select
                                className="crm-select"
                                value={filters.action}
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                            >
                                <option value="">All Actions</option>
                                <option value="create">Create</option>
                                <option value="update">Update</option>
                                <option value="delete">Delete</option>
                                <option value="merge">Merge</option>
                                <option value="import">Import</option>
                                <option value="export">Export</option>
                                <option value="upload">Upload</option>
                                <option value="download">Download</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="crm-card">
                <div className="crm-card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="crm-loading">
                            <div className="crm-spinner"></div>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="crm-empty-state">
                            <h3>No events found</h3>
                            <p>Try adjusting your filters.</p>
                        </div>
                    ) : (
                        <div className="crm-table-container">
                            <table className="crm-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '180px' }}>Time</th>
                                        <th style={{ width: '150px' }}>User</th>
                                        <th style={{ width: '100px' }}>Action</th>
                                        <th style={{ width: '120px' }}>Entity</th>
                                        <th>Summary</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((event) => (
                                        <tr key={event.id}>
                                            <td style={{ fontSize: '13px', color: 'var(--crm-gray-600)' }}>
                                                {formatTime(event.at)}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: '500' }}>{event.actorName || 'System'}</div>
                                            </td>
                                            <td>
                                                <span className={`crm-badge crm-badge-${event.action === 'delete' ? 'lost' : event.action === 'create' ? 'new' : 'qualified'}`}>
                                                    {event.action}
                                                </span>
                                            </td>
                                            <td>
                                                {getEntityLink(event.entityType, event.entityId) ? (
                                                    <span
                                                        className="crm-link"
                                                        onClick={() => history.push(getEntityLink(event.entityType, event.entityId))}
                                                        style={{ cursor: 'pointer', color: 'var(--crm-primary)' }}
                                                    >
                                                        {event.entityType}
                                                    </span>
                                                ) : (
                                                    <span style={{ textTransform: 'capitalize' }}>{event.entityType}</span>
                                                )}
                                            </td>
                                            <td>
                                                <div>{event.summary}</div>
                                                {event.changes && event.changes.length > 0 && (
                                                    <div style={{ fontSize: '12px', color: 'var(--crm-gray-500)', marginTop: '4px' }}>
                                                        Changed: {event.changes.map(c => c.field).join(', ')}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Simple Pagination */}
                <div className="crm-pagination" style={{ borderTop: '1px solid var(--crm-gray-200)', padding: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                        className="crm-btn crm-btn-secondary crm-btn-sm"
                        disabled={page === 1 || loading}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        Previous
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--crm-gray-700)' }}>
                        Page {page}
                    </span>
                    <button
                        className="crm-btn crm-btn-secondary crm-btn-sm"
                        disabled={events.length < pageSize || loading}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AuditPage;
